import { useMemo, useRef, useState } from "react";
import { useGetSet, useMount, useUnmount } from "react-use";
import {
  InputMuteUpdate,
  LiveChatJoinResponse,
  LivechatSubjectType,
  LiveChatUser,
  LiveChatUserUpdateAction,
  LiveChatUserUpdateEvent,
} from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import { debounce } from "lodash-es";
import useMounted from "hooks/useMounted";
import useOnAuthorised from "hooks/useOnAuthorised";
import providers from "lib/providers";

interface State {
  joinData: LiveChatJoinResponse | null;
  loading: boolean;
}

export type UserListUpdatedCB = (data: LiveChatUserUpdateEvent) => void;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const emptyFunc = () => {};

export default function useLiveChat({
  livechatSubject,
  connectionDelay = 0,
  liveChatName,
}: {
  livechatSubject: Subject<LivechatSubjectType>;
  connectionDelay?: number;
  liveChatName?: string;
}) {
  const keepAliveStart = useRef<boolean>(true);
  const refreshUsersTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mounted = useMounted();
  const [getState, setState] = useGetSet<State>({
    joinData: null,
    loading: false,
  });

  const temp = getState();
  const userList = useMemo(() => (temp.joinData?.users ?? []).filter((i) => i.id !== temp.joinData?.me.id), [temp]);

  const [clientServerTimeDiff, setClientServerTimeDiffState] = useState(0);
  const setClientServerTimeDiff = (serverTime: Date) => {
    setClientServerTimeDiffState(new Date().getTime() - serverTime.getTime());
  };

  const updateUserList = (data: LiveChatUserUpdateEvent) => {
    setState((state) => {
      if (!state.joinData) {
        return state;
      }
      let users = state.joinData.users;
      if (data.action === LiveChatUserUpdateAction.DELETE) {
        users = state.joinData.users.filter((i) => i.id !== data.user.id);
      }
      if (data.action === LiveChatUserUpdateAction.UPDATE) {
        if (state.joinData.me.id === data.user.id) {
          state.joinData.me = data.user;
        }
        if (state.joinData.users.find((i) => data.user.id === i.id)) {
          users = state.joinData.users.map((i) => (i.id === data.user.id ? data.user : i));
        } else users = state.joinData.users.concat([data.user]);
      }
      return {
        ...state,
        joinData: {
          ...state.joinData,
          users,
        },
      };
    });
  };

  const endChat = async () => {
    const state = getState();
    if (!state.joinData) return;
    setState((prevState) => ({
      ...prevState,
      joinData: null,
    }));
    return providers.LivechatsProvider.end({ token: state.joinData.token });
  };

  const onUserListUpdatedCB = useRef<UserListUpdatedCB>(emptyFunc);
  const setOnUserListUpdatedCB = (cb: UserListUpdatedCB) => (onUserListUpdatedCB.current = cb);
  const userListUpdateUnsubscribe = useRef(emptyFunc);
  const subscribeUserListUpdated = async (joinData: LiveChatJoinResponse) => {
    const token = joinData.token ?? "";
    const subscription = await providers.LivechatsProvider.subscribeUserUpdate(
      token,
      (data: LiveChatUserUpdateEvent) => {
        if (!mounted.current) return;
        onUserListUpdatedCB.current(data);
        updateUserList(data);
      }
    );
    if (!mounted.current) {
      return;
    }
    userListUpdateUnsubscribe.current = () => {
      subscription.unsubscribe();
    };
  };

  const refreshUsers = async () => {
    const users = await providers.LivechatsProvider.liveChatUsersSearch({
      data: { chatSubject: livechatSubject },
    });
    if (!mounted.current) return;
    setState((state) => {
      if (!state.joinData) return state;
      return {
        ...state,
        joinData: {
          ...state.joinData,
          users,
        },
      };
    });
  };

  const startUsersRefresh = () => {
    stopUsersRefresh();
    refreshUsersTimer.current = setInterval(refreshUsers, 10000);
  };

  const stopUsersRefresh = () => {
    if (refreshUsersTimer.current) {
      clearInterval(refreshUsersTimer.current);
      refreshUsersTimer.current = null;
    }
  };

  const keepAlive = async (token: string): Promise<void> => {
    await new Promise((r) => setTimeout(r, 1000));
    if (!keepAliveStart.current) return;
    await providers.LivechatsProvider.keepalive({ token });
    return keepAlive(token);
  };

  const toggleMuteUser = debounce(async (user: LiveChatUser) => {
    const data: InputMuteUpdate = {
      chatUserId: user.id,
      muted: !user.muted,
    };
    await providers.LivechatsProvider.muteUpdate({ data });
  }, 500);

  const onCreated = async () => {
    setState((s) => ({ ...s, loading: true }));
    await new Promise((r) => setTimeout(r, connectionDelay));
    const joinData = await providers.LivechatsProvider.join({
      data: {
        subject: livechatSubject,
        ...(liveChatName ? { screenName: liveChatName } : {}),
      },
    });
    try {
      mounted.current && setState((s) => ({ ...s, joinData }));
      setClientServerTimeDiff(parseISO(joinData.serverTime));
      await subscribeUserListUpdated(joinData);
      await refreshUsers();
      startUsersRefresh();
      keepAlive(joinData.token);
    } finally {
      mounted.current && setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  useMount(() => onCreated());
  useUnmount(() => {
    endChat();
    keepAliveStart.current = false;
    stopUsersRefresh();
    userListUpdateUnsubscribe.current();
  });

  useOnAuthorised(async () => {
    await endChat();
    await onCreated();
  });

  return {
    loading: getState().loading,
    joinData: getState().joinData,
    token: getState().joinData?.token ?? "",
    userList,
    setOnUserListUpdatedCB,
    clientServerTimeDiff,
    endChat,
    toggleMuteUser,
  };
}
